import React, { useEffect, useState } from "react";
import { Button, Divider, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ROOM_NUMBERING_METHODS,
  ZRoomNumberingMethodKey,
} from "@multiconsult-group/multiplacer-core";
import { openapi } from "../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectRoomNumberingRestrictionLinePlacementMode,
  selectRoomNumberingSelectedRestrictionLineId,
  selectRoomNumberingHeatmapVisible,
  selectRoomNumberingSelectedMethodKey,
  setHeatmapVisible,
  setNumberingOrigin,
  setRestrictionLines,
  setSelectedRoomNumberingMethodKey,
  toggleRestrictionLinePlacementMode,
  deleteRestrictionLine,
} from "../../../RoomNumbering/roomNumberingSlice";
import { ModuleToolBarShell } from "../ModuleToolBarShell";
import { thunkCalculateRoomNumbers } from "../../../RoomNumbering/roomNumberingThunks";
import { setIfcClassVisibilityInRedux } from "../../designSlice";
import { RoomNumberingExportModal } from "../../../RoomNumbering/RoomNumberingExportModal";
import { SelectedRoomsRoomNumberingActionsMenu } from "../../../RoomNumbering/SelectedRoomsRoomNumberingActionsMenu";
import {
  selectHasCachedRoomNumberingHeatmap,
  selectRoomNumberingCalculationMetadataApiArg,
} from "../../../RoomNumbering/roomNumberingSelectors";

const roomNumberingMethodOptions = ROOM_NUMBERING_METHODS.map((method) => ({
  value: method.key,
  label: method.title,
  description: method.summary,
}));

const toCoordinate = (value: number[] | null | undefined): [number, number] | null => {
  if (!Array.isArray(value) || value.length < 2) return null;
  const [x, y] = value;
  if (x == null || y == null) return null;
  return [x, y];
};

export const RoomNumberingBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const restrictionLinePlacementMode = useAppSelector(selectRoomNumberingRestrictionLinePlacementMode);
  const selectedRestrictionLineId = useAppSelector(selectRoomNumberingSelectedRestrictionLineId);
  const heatmapVisible = useAppSelector(selectRoomNumberingHeatmapVisible);
  const hasCachedHeatmap = useAppSelector(selectHasCachedRoomNumberingHeatmap);
  const selectedMethodKey = useAppSelector(selectRoomNumberingSelectedMethodKey);
  const metadataApiArg = useAppSelector(selectRoomNumberingCalculationMetadataApiArg);
  const { data: calculationMetadata } =
    openapi.useGetMultiplacerRoomsRoomNumberingCalculationMetadataQuery(
      metadataApiArg ?? skipToken,
    );
  const [modalOpen, setModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedMethod =
    ROOM_NUMBERING_METHODS.find((method) => method.key === selectedMethodKey) ??
    ROOM_NUMBERING_METHODS[0];

  useEffect(() => {
    dispatch(setIfcClassVisibilityInRedux({ ifcClass: "IfcDoor", visible: true }));
  }, [dispatch]);

  useEffect(() => {
    if (calculationMetadata == null) return;

    const numberingOrigin = toCoordinate(calculationMetadata.numberingOrigin);
    const restrictionLines = calculationMetadata.restrictionLines.flatMap((line, index) => {
      const start = toCoordinate(line.start);
      const end = toCoordinate(line.end);
      if (start == null || end == null) return [];

      return [{
        id: line.id ?? `metadata-${index}-${start.join("_")}-${end.join("_")}`,
        start,
        end,
      }];
    });

    dispatch(setNumberingOrigin(numberingOrigin));
    dispatch(setRestrictionLines(restrictionLines));
  }, [calculationMetadata, dispatch]);

  const handleMethodChange = (value: string | null) => {
    const result = ZRoomNumberingMethodKey.safeParse(value);
    if (result.success) {
      dispatch(setSelectedRoomNumberingMethodKey(result.data));
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(thunkCalculateRoomNumbers(selectedMethodKey));
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModuleToolBarShell>
        <Group align="center" gap="md" wrap="nowrap">
          <Group gap="sm" align="center" wrap="nowrap">
            <Text fw={600} size="sm">
              Romnummerering
            </Text>
            <Divider orientation="vertical" h={24} />
            <Text size="sm" c="dimmed">
              Beregn romnummer for aktiv etasje
            </Text>
          </Group>

          <Select
            aria-label="Romnummerering"
            data={roomNumberingMethodOptions}
            value={selectedMethodKey}
            onChange={handleMethodChange}
            allowDeselect={false}
            disabled={isSubmitting}
            size="sm"
            w={240}
          />
          <Button size="sm" onClick={() => setModalOpen(true)}>
            Beregn romnummer
          </Button>
          <Button size="sm" variant="light" onClick={() => setExportModalOpen(true)}>
            Eksporter
          </Button>
          <Divider orientation="vertical" h={24} />
          <Button
            size="sm"
            variant={restrictionLinePlacementMode ? "filled" : "light"}
            color={restrictionLinePlacementMode ? "dark" : "gray"}
            onClick={() => dispatch(toggleRestrictionLinePlacementMode())}
          >
            {restrictionLinePlacementMode ? "Avslutt hindring" : "Plasser hindring for løpenummer"}
          </Button>
          {selectedRestrictionLineId != null && (
            <Button
              size="sm"
              variant="light"
              color="red"
              onClick={() => dispatch(deleteRestrictionLine(selectedRestrictionLineId))}
            >
              Slett hindring
            </Button>
          )}
          {hasCachedHeatmap && (
            <Button
              size="xs"
              variant={heatmapVisible ? "filled" : "light"}
              onClick={() => dispatch(setHeatmapVisible(!heatmapVisible))}
            >
              {heatmapVisible ? "Varmekart på" : "Varmekart av"}
            </Button>
          )}
          <SelectedRoomsRoomNumberingActionsMenu />
        </Group>
      </ModuleToolBarShell>

      <RoomNumberingExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />

      <Modal
        opened={modalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setModalOpen(false);
          }
        }}
        title="Beregn romnummer"
        centered
      >
        <Stack gap="lg">
          <Text size="sm">Vil du beregne romnummer på nåværende etasje?</Text>
          <Select
            label="Romnummerering"
            data={roomNumberingMethodOptions}
            value={selectedMethodKey}
            onChange={handleMethodChange}
            allowDeselect={false}
            disabled={isSubmitting}
          />
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {selectedMethod.title}
            </Text>
            <Text size="sm" c="dimmed">
              Kunde: {selectedMethod.customer}
            </Text>
            <Text size="sm" c="dimmed">
              PDF: {selectedMethod.pdfFileName}
            </Text>
            {selectedMethod.summary != null && (
              <Text size="sm" c="dimmed">
                {selectedMethod.summary}
              </Text>
            )}
          </Stack>

          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={isSubmitting}>
              Avbryt
            </Button>
            <Button onClick={() => void handleConfirm()} loading={isSubmitting}>
              Beregn romnummer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
